import { useEffect, useRef } from "react";
import { requestApp, WEB_TO_APP } from "common/webview";
import {
    pushBackHandler,
    getActiveBackHandler as sharedGetActiveBackHandler,
    subscribeBackStackChange,
} from "wiz-front-shared/hooks/backHandlerStack";

/**
 * 안드로이드 device back 을 webview history(`webview.goBack()`) 의존 없이
 * 메시지(`APP_TO_WEB.PRESS_BACK`) 기반으로 가로채기 위한 page-level 등록 hook.
 *
 * 내부적으로 wiz-front-shared 의 backHandlerStack 을 사용 → 페이지/모달/팝업이 LIFO 로 쌓여
 * 키보드 → 모달 → 페이지 순으로 한 칸씩 dismiss 되는 표준 Android 패턴이 자연스럽게 구현됨.
 *
 * 사용 패턴:
 *   useBackInterceptor(() => {
 *       if (step === 6) setAbortOpen(true);
 *       else window.history.back();
 *   });
 *
 * 동작:
 *   - 스택이 1+ 일 때 네이티브에 `SET_BACK_INTERCEPT { enabled: true }` 전송 → device back 위임 받음
 *   - 스택이 비면 disabled → 기본 동작(webview.goBack/doubleBackExit) 복원
 *   - 마운트 시 push, 언마운트 시 pop. push/pop 발생 시 자동으로 enable/disable 토글.
 */

// 모듈 init 시 1회 — 스택 상태에 따라 SET_BACK_INTERCEPT 토글.
//   "현재 enabled 여부" 를 추적해 동일 값 중복 전송 방지.
//   useBackInterceptor.ts 는 useWebviewMessage 를 통해 routers 시작 시 import 되므로
//   subscription 이 일찍 setup 됨.
let lastEnabled = false;
subscribeBackStackChange(() => {
    const enabled = sharedGetActiveBackHandler() !== null;
    if (enabled === lastEnabled) return;
    lastEnabled = enabled;
    requestApp(WEB_TO_APP.SET_BACK_INTERCEPT, { enabled });
});

/** useWebviewMessage 등 외부에서 현재 등록된 최상위 핸들러를 호출 — 없으면 null. */
export const getActiveBackHandler = sharedGetActiveBackHandler;

export const useBackInterceptor = (handler: () => void) => {
    // 매 렌더의 최신 handler 를 ref 로 유지 — closure stale 방지
    const handlerRef = useRef(handler);
    useEffect(() => {
        handlerRef.current = handler;
    }, [handler]);

    useEffect(() => {
        const wrapper = () => handlerRef.current?.();
        return pushBackHandler(wrapper);
    }, []);
};
